import api from '@/lib/api';
import type {
  PreboardingConflictDto,
  PreboardingConflictResolution,
  PreboardingConflictSummary,
} from '@hygge/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const preboardingConflictsKeys = {
  all: ['preboarding-conflicts'] as const,
  employee: (employeeId: string) =>
    [...preboardingConflictsKeys.all, employeeId] as const,
  summary: (employeeId: string) =>
    [...preboardingConflictsKeys.all, employeeId, 'summary'] as const,
};

async function listForEmployee(employeeId: string): Promise<PreboardingConflictDto[]> {
  const { data } = await api.get<PreboardingConflictDto[]>(
    `/preboarding-forms/conflicts/employee/${employeeId}`,
  );
  return data;
}

async function getSummary(employeeId: string): Promise<PreboardingConflictSummary> {
  const { data } = await api.get<PreboardingConflictSummary>(
    `/preboarding-forms/conflicts/employee/${employeeId}/summary`,
  );
  return data;
}

async function resolveConflict(
  id: string,
  resolution: PreboardingConflictResolution,
): Promise<PreboardingConflictDto> {
  const { data } = await api.post<PreboardingConflictDto>(
    `/preboarding-forms/conflicts/${id}/resolve`,
    { resolution },
  );
  return data;
}

/** All conflicts (pending first) for an employee — powers the review modal. */
export function usePreboardingConflicts(employeeId: string, enabled = true) {
  return useQuery({
    queryKey: preboardingConflictsKeys.employee(employeeId),
    queryFn: () => listForEmployee(employeeId),
    enabled: enabled && !!employeeId,
  });
}

/** Pending count for the profile banner. */
export function usePreboardingConflictSummary(employeeId: string, enabled = true) {
  return useQuery({
    queryKey: preboardingConflictsKeys.summary(employeeId),
    queryFn: () => getSummary(employeeId),
    enabled: enabled && !!employeeId,
  });
}

export function useResolvePreboardingConflict(employeeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      resolution,
    }: {
      id: string;
      resolution: PreboardingConflictResolution;
    }) => resolveConflict(id, resolution),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: preboardingConflictsKeys.employee(employeeId) });
      queryClient.invalidateQueries({ queryKey: preboardingConflictsKeys.summary(employeeId) });
    },
  });
}
